import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Form, Input, Message, Modal } from '@bedrock/components';
import {
  Plus,
  SignIn,
  Save,
  Refresh,
  InfoCircleFilled,
} from '@bedrock/icons-react';
import classNames from 'classnames/bind';
import { ProTablePlusForm } from '@mpc/common/src/components';
import styles from './index.less';
import { useMemoizedFn } from 'ahooks';
import CustomInspectionTemplateSelect from '../CustomInspectionTemplateSelect';
import {
  demandSourceMatchTemplateDetail,
  DemandSourceMatchTemplateFieldEntity,
  demandSourceMatchTemplateUpdate,
} from '#services/api';
import SaveAsNewTemplate, {
  SaveAsNewTemplateRefType,
} from './SaveAsNewTemplate';
import { $sType } from '#utils';

const cx = classNames.bind(styles);

interface CustomField {
  id: string;
  field_name: string;
  plan_remark: string;
  value_example: string;
}

interface CustomInspectionFieldsProps {
  type: 'edit' | 'preview';
  value?: DemandSourceMatchTemplateFieldEntity[];
  showReferTemplateButton?: boolean; // 是否显示引用模版按钮
  showSaveTemplateButton?: boolean; // 是否显示保存更新原模版按钮
  showSaveAsNewTemplateButton?: boolean; // 是否显示存为新模版按钮
  defaultReferTemplateId?: number; // 默认引用模版ID
  onSelectTemplate?: (template: { id: number; name: string }) => void; // 选择模版回调
  onChange?: (value: DemandSourceMatchTemplateFieldEntity[]) => void; // 定制排查字段数据变化
  hasSavePermission?: boolean; // 是否具有保存权限
  defaultFilters?: Partial<Record<'main_platform' | 'product_id', any[]>>; // 默认筛选条件
  fixedFilters?: Partial<Record<'main_platform' | 'product_id', any[]>>; // 固定筛选条件
}

const CustomInspectionFields: React.FC<CustomInspectionFieldsProps> =
  forwardRef((props, ref) => {
    const {
      type,
      value = [{}],
      onChange,
      showReferTemplateButton = false,
      showSaveTemplateButton = false,
      showSaveAsNewTemplateButton = false,
      onSelectTemplate,
      defaultReferTemplateId,
      defaultFilters,
      fixedFilters,
      hasSavePermission = false,
    } = props;
    const proTableFormRef = useRef<any>(null);
    const [referedTemplate, setReferedTemplate] = useState<{
      id: number;
      name: string;
    }>(
      defaultReferTemplateId ? { id: defaultReferTemplateId, name: '' } : null
    );
    const [localData, setLocalData] =
      useState<DemandSourceMatchTemplateFieldEntity[]>(value);
    /** 存为新模版弹窗 */
    const saveAsNewTemplateRef = useRef<SaveAsNewTemplateRefType>(null);

    /** 设置默认引用模版 */
    useEffect(() => {
      if (
        defaultReferTemplateId &&
        defaultReferTemplateId !== referedTemplate?.id
      ) {
        demandSourceMatchTemplateDetail({ id: defaultReferTemplateId })
          .then((res: any) => {
            if (res.code === 1) {
              const data = res.data.demand_source_match_template;
              setReferedTemplate({
                id: defaultReferTemplateId,
                name: data.name,
              });
            }
          })
          .catch((error: any) => {
            console.error('获取详情失败:', error);
          });
      }
    }, [defaultReferTemplateId]);

    useImperativeHandle(ref, () => ({
      validateFields: async () => {
        try {
          if (proTableFormRef.current?.validateFieldsAndScroll) {
            const values =
              await proTableFormRef.current?.validateFieldsAndScroll?.();
            if (values?.[0]?.message) {
              return Promise.reject(values?.[0]?.message);
            }
          }
          return Promise.resolve();
        } catch (error) {
          console.log(error, 'error');
          return Promise.reject();
        }
      },
    }));

    // 处理定制排查字段数据变化
    const handleCustomFieldsChange = (
      newValue: DemandSourceMatchTemplateFieldEntity[]
    ) => {
      setLocalData(newValue);
      onChange?.(newValue);
    };

    // 处理批量编辑变化
    const handleBatchEditChange = (
      newData: DemandSourceMatchTemplateFieldEntity[]
    ) => {
      setLocalData(newData);
      onChange?.(newData);
    };

    // 添加字段
    const addCustomField = () => {
      if (proTableFormRef.current?.addRow) {
        const newData = {
          id: Date.now().toString(),
          field_name: '',
          plan_remark: '',
          value_example: '',
        };
        proTableFormRef.current.addRow(newData);
      }
    };

    // 定义 ProTablePlusForm 的列配置
    const customFieldColumns = useMemo(
      () => [
        {
          title: '字段名称',
          dataIndex: 'field_name',
          width: 200,
          minWidth: 200,
          render: (v: any, r: any, i: number) => (
            <span style={{ fontWeight: 'bold' }}>{v || '--'}</span>
          ),
          editRender: () => ({
            name: 'input',
            editable: type !== 'preview',
            showReadonlyState: true,
            props: {
              placeholder: '请输入',
              maxLength: 50,
              showCount: true,
            },
            rule: {
              required: type !== 'preview',
              message: '字段名称不能为空',
            },
          }),
        },
        {
          title: $sType === 'inner' ? '策划备注要求' : '备注要求',
          dataIndex: 'plan_remark',
          minWidth: 320,
          render: (v: any, r: any, i: number) => v || '--',
          editRender: () => ({
            name: 'input',
            editable: type !== 'preview',
            showReadonlyState: true,
            props: {
              placeholder: '请输入',
              maxLength: 200,
              showCount: true,
            },
          }),
        },
        {
          title: $sType === 'inner' ? '供应商填写样例' : '填写样例',
          dataIndex: 'value_example',
          minWidth: 320,
          render: (v: any, r: any, i: number) => v || '--',
          editRender: () => ({
            name: 'input',
            editable: type !== 'preview',
            showReadonlyState: true,
            props: {
              placeholder: '请输入',
              maxLength: 200,
              showCount: true,
            },
          }),
        },
        {
          title: '操作',
          dataIndex: 'operate',
          width: 52,
          hideInTable: type === 'preview',
          render: (v: any, row: any, index: number) => {
            return (
              <Button
                type="link"
                danger
                disabled={!localData || localData.length <= 1}
                onClick={() => {
                  const data = proTableFormRef.current.getData();
                  if (
                    proTableFormRef.current?.removeRow &&
                    data &&
                    data.length > 1
                  ) {
                    proTableFormRef.current.removeRow(index);
                  }
                }}
              >
                删除
              </Button>
            );
          },
        },
      ],
      [type, localData]
    );

    /** 引用模版 */
    const referToTemplate = useMemoizedFn(() => {
      console.log('referTemplate');
      CustomInspectionTemplateSelect.show({
        onOk: onTemplateSelect,
        defaultFilters: defaultFilters,
        fixedFilters: fixedFilters,
      });
    });

    /** 保存更新原模版 */
    const saveTemplate = useMemoizedFn(async () => {
      if (referedTemplate?.id) {
        try {
          await proTableFormRef.current?.validateFields?.();

          Modal.confirm({
            status: 'info',
            okText: '确定',
            cancelText: '取消',
            title: `确定更新原“${referedTemplate?.name}”模版内容吗？`,
            icon: <InfoCircleFilled />,
            className: cx('update-template-modal'),
            onOk: async () => {
              try {
                await demandSourceMatchTemplateUpdate({
                  id: referedTemplate?.id,
                  demand_source_match_template_field_entities: localData,
                });
                Message.success('保存成功');
              } catch (error) {
                console.error('保存失败:', error);
                return;
              }
            },
          });
        } catch (error) {
          console.error('保存失败:', error);
          return;
        }
      }
    });

    /** 存为新模版 */
    const saveAsNewTemplate = useMemoizedFn(() => {
      proTableFormRef.current
        ?.validateFields?.()
        .then((res: any) => {
          saveAsNewTemplateRef.current?.open(localData);
        })
        .catch((error: any) => {
          console.error('保存失败:', error);
        });
    });

    /** 引用模版选择回调 */
    const onTemplateSelect = useMemoizedFn((ids: number[]) => {
      const id = ids[0];
      if (id) {
        demandSourceMatchTemplateDetail({ id })
          .then((res: any) => {
            if (res.code === 1) {
              const data = res.data.demand_source_match_template;
              setReferedTemplate({ id, name: data.name });
              handleCustomFieldsChange(
                data.demand_source_match_template_field_entities || []
              );
              onSelectTemplate?.({ id, name: data.name });
            }
          })
          .catch((error: any) => {
            console.error('获取详情失败:', error);
          });
      }
    });

    return (
      <>
        <div style={{ width: '100%', maxWidth: '100%' }}>
          <ProTablePlusForm
            ref={proTableFormRef}
            value={value}
            scroll={{ x: 'max-content' }}
            onChange={handleCustomFieldsChange}
            onBatchEditChange={handleBatchEditChange}
            columns={customFieldColumns}
            emptyText="暂无数据"
            className={cx('custom-fields-table', 'w-full')}
            rowKey="__table_row_key__"
            pagination={false}
            manualValidate={true}
            autoThroughRowSpan={false}
            sortable={type !== 'preview'}
            // footer={
            //   type === 'preview'
            //     ? null
            //     : () => {
            //         return (
            //           <div className="flex items-center gap-x-[12px]">
            //             <Button
            //               type="checked"
            //               size="small"
            //               icon={<Plus />}
            //               onClick={addCustomField}
            //             >
            //               添加字段
            //             </Button>
            //             {showReferTemplateButton && (
            //               <Button
            //                 type="checked"
            //                 size="small"
            //                 icon={<SignIn />}
            //                 onClick={referToTemplate}
            //               >
            //                 引用模版
            //               </Button>
            //             )}
            //             {showSaveTemplateButton && (
            //               <Button
            //                 type="checked-neutral"
            //                 size="small"
            //                 icon={<Refresh />}
            //                 onClick={saveTemplate}
            //                 disabled={
            //                   !referedTemplate?.id || !hasSavePermission
            //                 }
            //               >
            //                 保存更新原模版
            //               </Button>
            //             )}
            //             {showSaveAsNewTemplateButton && (
            //               <Button
            //                 type="checked-neutral"
            //                 size="small"
            //                 icon={<Save />}
            //                 onClick={saveAsNewTemplate}
            //                 disabled={
            //                   !localData?.length ||
            //                   localData?.every((item) => !item.field_name) ||
            //                   !hasSavePermission
            //                 }
            //               >
            //                 存为新模版
            //               </Button>
            //             )}
            //           </div>
            //         );
            //       }
            // }
          />
          {type === 'preview' ? null : (
            <Button
              type="checked"
              size="small"
              icon={<Plus />}
              onClick={addCustomField}
              className='m-t-8'
            >
              添加字段
            </Button>
          )}
        </div>
        <SaveAsNewTemplate ref={saveAsNewTemplateRef} />
      </>
    );
  });

export default CustomInspectionFields;
